import { EProblemReportDateFilter } from '@constants';
import { DatePicker, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DateRangeWrapper,
  DropdownWrapper,
  LabelWrapper,
  OptionsWrapper,
  Wrapper
} from './ProblemReportDateFilter.styles';

import { Icon } from '@/components/Icon';

type Props = {
  value?: EProblemReportDateFilter;
  dateRange?: [string, string];
  onChange?: (value?: EProblemReportDateFilter) => void;
  onDateRangeChange?: (dateRange?: [string, string]) => void;
  disabled?: boolean;
};

const DATE_FILTER_OPTIONS = [
  EProblemReportDateFilter.ALL,
  EProblemReportDateFilter.LAST_7_DAYS,
  EProblemReportDateFilter.LAST_30_DAYS,
  EProblemReportDateFilter.THIS_MONTH,
  EProblemReportDateFilter.THIS_QUARTER,
  EProblemReportDateFilter.THIS_YEAR,
  EProblemReportDateFilter.CUSTOM_RANGE
];

export const ProblemReportDateFilter: React.FC<Props> = ({
  value,
  dateRange,
  onChange,
  onDateRangeChange,
  disabled = false
}) => {
  const { t } = useTranslation('ProblemReportsTranslations');
  const [isOpen, setIsOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(
    value === EProblemReportDateFilter.CUSTOM_RANGE
  );

  const handleSelect = (selectedValue: EProblemReportDateFilter) => {
    if (selectedValue === EProblemReportDateFilter.CUSTOM_RANGE) {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
      onChange?.(selectedValue === EProblemReportDateFilter.ALL ? undefined : selectedValue);
      onDateRangeChange?.(undefined);
      setIsOpen(false);
    }
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      onChange?.(EProblemReportDateFilter.CUSTOM_RANGE);
      onDateRangeChange?.([dates[0].toISOString(), dates[1].toISOString()]);
      setIsOpen(false);
    } else {
      onDateRangeChange?.(undefined);
    }
  };

  const handleDropdownVisibleChange = (visible: boolean) => {
    if (!visible && isDatePickerOpen) {
      return;
    }
    setIsOpen(visible);
  };

  const handleClear = () => {
    setShowDatePicker(false);
    onChange?.(undefined);
    onDateRangeChange?.(undefined);
  };

  const getDisplayLabel = () => {
    if (!value || value === EProblemReportDateFilter.ALL) {
      return t('DATE_FILTER_ALL');
    }
    if (value === EProblemReportDateFilter.CUSTOM_RANGE && dateRange) {
      return `${dayjs(dateRange[0]).format('DD/MM')} - ${dayjs(dateRange[1]).format('DD/MM')}`;
    }
    return t(`DATE_FILTER_${value}`);
  };

  return (
    <Wrapper $hasValue={!!value && value !== EProblemReportDateFilter.ALL}>
      <Select
        allowClear
        onClear={handleClear}
        variant="borderless"
        open={isOpen}
        onOpenChange={handleDropdownVisibleChange}
        value={value}
        styles={{ popup: { root: { minWidth: 260 } } }}
        labelRender={() => (
          <LabelWrapper>
            <span>{getDisplayLabel()}</span>
          </LabelWrapper>
        )}
        suffixIcon={<Icon i="CaretDown" />}
        placeholder={t('DATE_FILTER_ALL')}
        disabled={disabled}
        dropdownRender={() => (
          <DropdownWrapper onClick={e => e.stopPropagation()}>
            <OptionsWrapper>
              {DATE_FILTER_OPTIONS.map(option => (
                <div
                  key={option}
                  className={`option ${
                    value === option ||
                    (option === EProblemReportDateFilter.CUSTOM_RANGE && showDatePicker)
                      ? 'selected'
                      : ''
                  }`}
                  onClick={() => handleSelect(option)}>
                  {t(`DATE_FILTER_${option}`)}
                </div>
              ))}
            </OptionsWrapper>
            {showDatePicker && (
              <DateRangeWrapper>
                <DatePicker.RangePicker
                  value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : undefined}
                  onChange={handleDateRangeChange}
                  onOpenChange={setIsDatePickerOpen}
                  format="DD/MM/YYYY"
                  placeholder={[t('START_DATE'), t('END_DATE')]}
                />
              </DateRangeWrapper>
            )}
          </DropdownWrapper>
        )}
      />
    </Wrapper>
  );
};
